<template>
  <div>
    <app-header :title="getLabel('USERS')">
      <template #col-right>
        <div
          v-if="
            auth.user.selectedAccess.roleLevel >= 3 &&
            auth.user.selectedAccess.can_modify_settings
          "
        >
          <b-button variant="light" class="mr-1" @click="preExportXLS()">
            <span v-label-key="'EXPORT_XLSX'">Export XLSX</span>
          </b-button>
          <b-button v-router-link="{ name: 'user-editor' }" variant="primary">
            <span v-label-key="'NEW_USER'">New User</span>
          </b-button>
        </div>
      </template>
    </app-header>
    <b-container>
      <b-row>
        <b-col>
          <search-box v-model="query" />
        </b-col>
      </b-row>
      <b-table
        id="user-table"
        ref="userTable"
        :per-page="0"
        :current-page="currentPage"
        :items="users"
        :fields="fields"
        :busy="loading"
        show-empty
        striped
        hover
        responsive
        @sort-changed="handleSortChange"
      >
        <template v-slot:cell(first_name)="data">
          <router-link :to="{ name: 'user-detail', params: data.item }">
            {{
              data.item.first_name || getLabel("UNNAMED_USER") || "Unnamed User"
            }}
            {{ data.item.last_name }}
          </router-link>
        </template>
        <template v-slot:cell(roles)="data">
          <div v-for="role in data.item.roles" :key="role.id">
            {{ role.name || "GoKaizen" }} ({{ roleName(role.role) }})
          </div>
        </template>
        <template v-slot:table-busy>
          <loading-spinner />
        </template>
      </b-table>
      <b-pagination
        v-model="currentPage"
        :total-rows="total"
        :per-page="perPage"
        align="right"
        aria-controls="user-table"
        @change="pageChanged"
      />
    </b-container>
  </div>
</template>

<script>
import debounce from "lodash.debounce";
import $api from "@/services/api";
import $auth from "@/services/auth";
import $search from "@/services/search";
import LoadingSpinner from "@/components/LoadingSpinner";
import SearchBox from "@/components/SearchBox";
import AppHeader from "@/components/AppHeader";
import { mapGetters } from "vuex";

const roleMap = {
  superadmin: "SUPERADMIN",
  distributor: "DISTRIBUTOR",
  subdistributor: "SUBDISTRIBUTOR",
  chain: "CHAIN",
  store: "STORE",
};

export default {
  components: {
    LoadingSpinner,
    SearchBox,
    AppHeader,
  },

  data() {
    return {
      auth: $auth,
      query: null,
      users: null,
      search: $search,
      perPage: 10,
      currentPage: 1,
      total: 0,
      orderBy: '',
      isOrderByDesc: false,
      loading: false,
    };
  },

  computed: {
    ...mapGetters("translations", {
      getLabel: "getLabel",
      getReplaceLabel: "getReplaceLabel",
    }),
    fields() {
      const fields = [
        {
          key: "first_name",
          label: this.getLabel("NAME") || "Name",
          sortable: true,
        },
        {
          key: "email",
          label: this.getLabel("EMAIL") || "Email",
          sortable: true,
        },
        {
          key: "roles",
          label: this.getLabel("ROLES") || "Roles",
        },
      ];
      return fields;
    },
    totalRows() {
      return this.users && this.search(this.users, this.query).length;
    },
    roleName() {
      return (role) => {
        if (!role) return "";
        return this.getLabel(roleMap[role]);
      };
    },
  },
  watch: {
    query(newValue) {
      this.debounceSearch(newValue);
    },
  },
  beforeDestroy() {
    if (this.debounceSearch && this.debounceSearch.cancel) {
      this.debounceSearch.cancel();
    }
  },
  created() {
    this.debounceSearch = debounce(() => {
      this.currentPage = 1;
      this.refreshData();
    }, 500);
    this.refreshData();
  },
  methods: {
    pageChanged(page) {
      this.currentPage = page;
      this.refreshData();
    },
    handleSortChange(e){
      this.orderBy = e.sortBy;
      this.isOrderByDesc = e.sortDesc;
      this.refreshData();
    },
    refreshData() {
      this.loading = true;
      this.users = [];

      let tempQuery = this.getListQuery();

      tempQuery += `&skip=${(this.currentPage - 1) * this.perPage}&take=${
        this.perPage
      }`;

      $api
        .get(
          "/v1/dashboard/dashboardusers" + tempQuery
        )
        .then((response) => {
          this.users = response.data;
          this.total = response.total;
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false;
          this.$refs.userTable.refresh();
        });
    },
    preExportXLS() {
      $api
        .get(
          "/v1/dashboard/dashboardusers"
        )
        .then((response) => {
          this.exportXLS(response.data);
        })
        .catch(() => {})
        .finally(() => {
        });
    },
    exportXLS(userData) {
      /* eslint-disable */
      const wb = XLSX.utils.book_new();
      const data = [
        [
          this.getLabel("ID") || "id",
          this.getLabel("NAME") || "name",
          this.getLabel("EMAIL") || "email",
          this.getLabel("ROLES") || "roles",
        ],
      ];
      userData.forEach((user) => {
        const name = `${
          user.first_name || this.getLabel("UNNAMED_USER") || "Unnamed User"
        } ${user.last_name}`;
        const roles = user.roles
          .map(
            (role) =>
              `${role.name || "GoKaizen"} (${this.roleName(role.role)})`,
          )
          .join(", ");
        data.push([user.id, name, user.email, roles]);
      });
      const ws = XLSX.utils.aoa_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, this.getLabel("USERS") || "Users");
      XLSX.writeFile(wb, "users.xlsx");
    },
    getListQuery() {
      const payload = {
        orderBy: this.orderBy? this.orderBy : null,
        isOrderByDesc: this.orderBy ? this.isOrderByDesc : null,
        searchKey: this.query ? this.query : null,
      };

      let tempQuery = "?";
      for (const key in payload) {
        if (payload[key] !== null && payload[key] !== undefined) {
          tempQuery += `&${key}=${payload[key]}`;
        }
      }

      return tempQuery;
    },
  },
};
</script>

<style scoped>
a {
  color: inherit;
}
</style>
